import { useState } from 'react';

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: '01',
      title: 'Piyasa Verileri Sürekli Analiz Edilir',
      description: 'BAGA, yüzlerce hisse senedinin fiyat, hacim ve teknik göstergelerini gerçek zamanlı olarak toplar ve işler.',
      icon: 'ri-line-chart-line',
      image: 'https://public.readdy.ai/ai/img_res/1afcfb3629091d9e9021e50f1df6f254.jpg',
      features: ['Gerçek Zamanlı Veri', 'Teknik Göstergeler', 'Hacim Analizi']
    },
    {
      number: '02',
      title: 'Algoritma Karar Üretir',
      description: 'Toplanan veriler, gelişmiş algoritmalar tarafından değerlendirilir ve ALIM veya İZLE kararları oluşturulur.',
      icon: 'ri-cpu-line',
      image: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20algorithm%20processing%20financial%20data%20with%20neural%20network%20visualization%2C%20emerald%20green%20glowing%20nodes%20and%20connections%20on%20dark%20background%2C%20futuristic%20AI%20decision%20making%20system%20for%20trading%20signals%2C%20abstract%20tech%20illustration%20with%20buy%20and%20watch%20indicators%2C%20modern%20fintech%20concept%20art&width=256&height=192&seq=algorithm-decision-02&orientation=landscape',
      features: ['Yapay Zeka', 'Otomatik Karar', 'Akıllı Filtreler']
    },
    {
      number: '03',
      title: 'Sonuçlar Yayın Sayfasında Canlı Listelenir',
      description: 'Üretilen kararlar, kullanıcıların erişebileceği canlı yayın sayfasında anlık olarak güncellenir.',
      icon: 'ri-broadcast-line',
      image: 'https://readdy.ai/api/search-image?query=live%20trading%20signals%20broadcast%20dashboard%20with%20real-time%20stock%20recommendations%20list%2C%20emerald%20green%20highlights%20on%20dark%20interface%2C%20modern%20fintech%20platform%20showing%20buy%20and%20watch%20alerts%20updating%20in%20real-time%2C%20clean%20professional%20trading%20signal%20feed%20with%20live%20status%20indicators%2C%20minimalist%20financial%20technology%20design&width=256&height=192&seq=live-broadcast-03&orientation=landscape',
      features: ['Canlı Yayın', 'Anlık Güncelleme', 'Kolay Erişim']
    },
  ];

  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-dark via-dark-lighter to-dark relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-emerald/35 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-emerald-light/30 rounded-full blur-[120px]"></div>
        <div className="absolute top-1/2 left-1/3 w-[350px] h-[350px] bg-emerald/20 rounded-full blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald/10 border border-emerald/20 rounded-full mb-6">
            <i className="ri-lightbulb-line text-emerald"></i>
            <span className="text-sm font-medium text-beige-light">Süreç</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-beige-light mb-4">
            Nasıl Çalışır?
          </h2>
          <p className="text-xl text-beige max-w-2xl mx-auto">
            BAGA'nın yapay zeka destekli karar sistemi 3 adımda çalışır
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-6xl mx-auto space-y-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className="group relative"
              onMouseEnter={() => setActiveStep(index)}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8 bg-dark-card border border-dark-border rounded-2xl p-8 hover:border-emerald/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald/10">
                {/* Step Number & Content */}
                <div className="flex-1 w-full">
                  <div className="flex items-start gap-6">
                    {/* Number Badge */}
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-emerald to-emerald-light rounded-xl flex items-center justify-center text-2xl font-bold text-dark shadow-lg shadow-emerald/30">
                      {step.number}
                    </div>

                    {/* Text Content */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-beige-light mb-3 group-hover:text-emerald transition-colors">
                        {step.title}
                      </h3>
                      <p className="text-beige leading-relaxed mb-4">
                        {step.description}
                      </p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        {step.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-emerald/10 border border-emerald/20 rounded-full text-sm text-beige-light"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className="w-full lg:w-80 h-64 flex-shrink-0 rounded-xl overflow-hidden bg-dark-lighter border border-dark-border">
                  <img
                    src={step.image}
                    alt={step.title}
                    className={`w-full h-full object-cover transition-all duration-500 ${
                      activeStep === index ? 'scale-105 opacity-100' : 'scale-100 opacity-70'
                    }`}
                  />
                </div>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute left-8 top-full w-0.5 h-8 bg-gradient-to-b from-emerald/50 to-transparent -translate-x-1/2"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}